import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import { ServiceProvider } from '@wsphere/service-providers/domain';
import { catchError, map, of, shareReplay, startWith, Subject, switchMap } from 'rxjs';
import { ServiceProviderService } from '../../services';

/** What the picker shows for the selection; a claim's snapshot fits too. */
export type ServiceProviderPickerValue = Pick<ServiceProvider.Model, 'id' | 'name' | 'phone' | 'address'>;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-service-provider-picker',
  templateUrl: './service-provider-picker.component.html',
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: ServiceProviderPickerComponent }],
})
export class ServiceProviderPickerComponent implements ControlValueAccessor {
  constructor(
    private cdr: ChangeDetectorRef,
    private serviceProviderSvc: ServiceProviderService
  ) {}

  @Input() disabled = false;

  value: ServiceProviderPickerValue | null = null;
  isSearching = false;
  isListOpen = false;

  readonly searchInput$ = new Subject<string>();

  // Null = still loading; [] = no match.
  readonly results$ = this.searchInput$.pipe(
    debouncedSearch(),
    startWith(''),
    switchMap(search =>
      this.serviceProviderSvc.claimLookup$(search).pipe(
        map(response => response.items),
        catchError(() => of([] as ServiceProvider.Model[])),
        startWith(null)
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private onChange: (value: ServiceProviderPickerValue | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: ServiceProviderPickerValue | null) {
    this.value = value;
    this.isSearching = !value;
    this.isListOpen = false;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: ServiceProviderPickerValue | null) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  select(provider: ServiceProviderPickerValue | null) {
    this.value = provider;
    this.isSearching = !provider;
    this.isListOpen = false;
    this.onChange(provider);
    this.onTouched();
  }

  /** Phone and city, e.g. "(555) 123-4567 · Austin, TX". */
  describe(provider: ServiceProviderPickerValue) {
    const place = [provider.address?.city, provider.address?.state].filter(Boolean).join(', ');
    return [provider.phone, place].filter(Boolean).join(' · ');
  }
}
